import { times, first, map } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { getBaseSignature } from "selectors/base"
import { getContentSets } from "selectors/securities-table"
import { getPortfolioType, getPortfolioId } from "modules/portfolio-root/selectors/portfolios"
import { getItems } from "./selectors/items"
import { getSelectedGroup, getGroups } from "./selectors/metrics"
import { getPortfolioNetLong } from "./selectors/market-values"
import { getPositionSet } from "helpers/comparison"
import { getQueryGroupBy } from "helpers/rollups"
import { getDetailsItems, getDetailsData, getChartDots, getFocusedLegendItem, getHoveredLegendItem, getFrontierSettingsPanelOpen, getCurrency } from "./selectors/details"
import { getCurrentConstraints, getSelectedCustomForecast, getCurrentHorizon, getActivatedConstraints } from "./selectors/constraints"
import { getData, getStatus, getReloadChart, getPortfolioData, getComparisonItemsStatus, getForecasts } from "./selectors/data"
import { Creators } from "./model"
import { classificationsQuery, generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { VIEW_TYPES } from "helpers/base"
import { getFileName } from "modules/comparison/selectors/"
import { MODEL_METADATA_QUERY } from "resources/models"

export default createModule({
  view,
  externalProps: (state, props) => ({
    accountId: state.global.selected.accountId,
    portfolioUUID: getPortfolioId(state),
    selectedGroup: getSelectedGroup(state, props),
    baseSignature: getBaseSignature(state),
    horizon: getCurrentHorizon(state),
    selectedCustomForecast: getSelectedCustomForecast(state),
    contentSets: getContentSets(state),
    classifications: getClassifications(state),
    forecasts: getForecasts(state), // implicitly needed by getSelectedCustomForecast
    currency: getCurrency(state, props),
  }),
  required: ["account", "modelId", "positionSets", "startDate", "endDate", "forecasts"],
  data: [...times(5, (n) => {
    return {
      resource: `compareFrontierPositionSet${n}`,
      as: null,
      passthrough: n > 0,
      url: ({ modelId, positionSets, startDate, endDate, base, viewType, selectedGroup, frontierDate, currency }) => {
        if (!frontierDate || !positionSets[n] || positionSets[n].uploading) {
          return null
        }

        const positionSet = getPositionSet(positionSets, n, startDate, { currency })

        // If a segment is applied on the positionSet, use that segment on the base
        const baseCopy = { ...base }
        if (positionSet.segment) {
          baseCopy.segment = positionSet.segment
        }

        const compositionPositionSet = getPositionSet(positionSets, n, startDate, { composition: true, currency })
        const { groupBy, tier, groupById } = getQueryGroupBy(selectedGroup)

        let contributionBy = ""
        let compositionQueryVariables = ""
        if (groupBy !== "LONG_SHORT") {
          compositionQueryVariables = ", $groupBy: ContributorGroupType! $groupById: String, $tier: String"
          contributionBy = `
            compositionBy (groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
              id
              description
              economicExposure
            }
          `
        }

        return {
          id: `${modelId}${JSON.stringify(positionSet)}${startDate}${endDate}${frontierDate}${viewType === VIEW_TYPES.ACTIVE ? JSON.stringify(baseCopy) : ""}${groupBy}${tier}${groupById}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $compositionPositionSet: PositionSetInput!, $base: [PositionSetInput], $frontierDate: Date! ${compositionQueryVariables} ) {
            model (id: $modelId) {
              compositionNonActive: simulation (positionSet: $compositionPositionSet, from: $frontierDate, to: $frontierDate, interval: AUTO) {
                composition (scaleFormat: DEFAULT) {
                  referenceEquity
                  modeledGmv
                  positions {
                    id
                    assetClass
                    economicExposure
                    description
                  }
                }
              }
              simulation (positionSet: $positionSet, base: $base, from: $frontierDate, to: $frontierDate, interval: AUTO) {
                risk {
                  date
                  total
                  attribution {
                    summary {
                      factors
                      specific
                    }
                  }
                }
              }
              compositionSimulation: simulation (positionSet: $compositionPositionSet, base: $base, from: $frontierDate, to: $frontierDate, interval: AUTO) {
                composition (scaleFormat: PERCENT_EQUITY_GMV) {
                  gmv
                  positionsCount
                  concentration {
                    min
                    max
                  }
                  longShortComposition: compositionBy (groupBy: LONG_SHORT) {
                    id
                    description
                    economicExposure
                  }
                  ${contributionBy}
                }
              }
            }
          }`,
          variables: {
            modelId,
            positionSet,
            compositionPositionSet,
            frontierDate,
            base: viewType === VIEW_TYPES.ACTIVE ? [baseCopy] : undefined,
            ...(contributionBy && { groupBy, tier, groupById }),
          },
        }
      },
    }
  }), {
    resource: "experimentPortfolioData",
    as: null,
    url: ({ modelId, positionSets, frontierDate, viewType, base, contentSets, classifications }) => {
      if (!frontierDate) return null

      const positionSet = first(positionSets)
      return {
        id: `${modelId}${JSON.stringify(positionSet)}${frontierDate}${viewType === VIEW_TYPES.ACTIVE ? JSON.stringify(base) : ""}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $portfolioId: String!, $frontierDate: Date!) {
          portfolio (id: $portfolioId) {
            dates (from: $frontierDate, to: $frontierDate, modelId: $modelId) {
              equity
              gmv
              equities {
                id { ticker mic modelProviderId }
                economicExposure
                country
                currency
                ${generateClassificationQueryForContributors("GICS", classifications)}
              }
              currencies { id economicExposure }
              swaps { id economicExposure }
              fixedIncome { id { isin } economicExposure }
              commodities { id economicExposure }
              indexes { id economicExposure }
              otherAssets { id economicExposure }
            }
          }
          model (id: $modelId) {
            portfolio (id: $portfolioId) {
              coverage (from: $frontierDate, to: $frontierDate) {
                summary {
                  percentGmvAvailable
                }
              }
            }
            simulation (positionSet: $positionSet, base: $base, from: $frontierDate, to: $frontierDate, interval: AUTO) {
              exposure {
                factors {
                  id
                  value: net
                  name
                  category
                }
              }
              ${contentSets.length > 0 ? map(contentSets, ({ id, escapedId }) => `${escapedId}: exposure (contentSetId: "${id}") { factors { id, value: net, name, category } } `) : ""}
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: { id: positionSet.id, type: "PORTFOLIO" },
          portfolioId: positionSet.id,
          frontierDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
        },
      }
    },
  }, {
    resource: "experimentForecastData",
    as: null,
    url: ({ modelId, positionSets, frontierDate, viewType, base, baseSignature, horizon, selectedCustomForecast }) => {
      baseSignature = viewType === VIEW_TYPES.ACTIVE ? baseSignature : ""
      base = viewType === VIEW_TYPES.ACTIVE ? base : null

      const positionSet = first(positionSets)
      return {
        id: `forecastData-${modelId}${positionSet.id}${frontierDate}${horizon}${baseSignature}${selectedCustomForecast}`,
        query: `query ($modelId: String!, $portfolioId: String!, $on: Date!, $base: [PositionSetInput], $horizon: Int!) {
          model (id: $modelId) {
            simulation (positionSet: {
              id: $portfolioId,
              type: PORTFOLIO
            }, base: $base, from: $on, to: $on) {
              forecast(on: $on, horizon: $horizon) {
                forecastData: ${selectedCustomForecast ? `custom(id: "${selectedCustomForecast}")` : "impliedReturns (riskFactors: MARKET)"} {
                  horizon
                  total
                  equities {
                    id {
                      ticker
                      mic
                      modelProviderId
                    }
                    expectedPercentReturn
                  }
                  swaps { id expectedPercentReturn }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          portfolioId: positionSet.id,
          on: frontierDate,
          base,
          horizon,
        },
      }
    },
  }, {
    resource: "compareFrontierConstraintsPreferences",
    url: ({ accountId, portfolioUUID, modelId }) => {
      if (!portfolioUUID) {
        return null
      }

      return ({
        id: `compareFrontierConstraintsPreferences-${accountId}${portfolioUUID}${modelId}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [
            {
              type: "compareFrontierConstraints",
              key: `${portfolioUUID}`,
              accountId,
            }, {
              type: "compareFrontierForecast",
              key: `${portfolioUUID}`,
              accountId,
            }, {
              type: "compareFrontierExposures",
              key: `${portfolioUUID}:${modelId}`,
              accountId,
            },
          ],
        },
      })
    },
  }, {
    resource: "classifications",
    as: null,
    url: () => {
      return {
        id: "classifications",
        query: classificationsQuery,
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    items: getItems(state, props),
    detailsItems: getDetailsItems(state, props),
    detailsData: getDetailsData(state, props),
    groups: getGroups(state, props),
    data: getData(state),
    comparisonItemsStatus: getComparisonItemsStatus(state),
    status: getStatus(state),
    dots: getChartDots(state, props),
    focusedLegendItem: getFocusedLegendItem(state),
    hoveredLegendItem: getHoveredLegendItem(state),
    frontierSettingsPanelOpen: getFrontierSettingsPanelOpen(state),
    currentConstraints: getCurrentConstraints(state),
    portfolioType: getPortfolioType(state),
    portfolioData: getPortfolioData(state),
    reloadChart: getReloadChart(state),
    portfolioNetLong: getPortfolioNetLong(state),
    activatedConstraints: getActivatedConstraints(state),
    fileName: getFileName(state, props),
  }),
  actions: Creators,
})
